import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, Clock, Save, Camera, Shield, 
  MessageCircle, Calendar, CheckCircle, X, ExternalLink, Copy, CalendarClock
} from 'lucide-react';

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday', short: 'Mon' },
  { id: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { id: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { id: 'thursday', label: 'Thursday', short: 'Thu' },
  { id: 'friday', label: 'Friday', short: 'Fri' },
  { id: 'saturday', label: 'Saturday', short: 'Sat' },
  { id: 'sunday', label: 'Sunday', short: 'Sun' },
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({
      value: `${hour}:${minute}`,
      label: `${displayHour}:${minute} ${period}`
    });
  }
}

export const ProfilePage = () => {
  const { user, api, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    avatar: '',
    admin_pin: '',
    chat_available: false,
    chat_availability: {}
  });
  
  // Booking availability state
  const [bookingAvailability, setBookingAvailability] = useState({
    available_days: {
      monday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
      tuesday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
      wednesday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
      thursday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
      friday: { enabled: true, slots: [{ start: '09:00', end: '17:00' }] },
      saturday: { enabled: false, slots: [] },
      sunday: { enabled: false, slots: [] }
    },
    appointment_durations: [15, 30, 45, 60],
    buffer_time: 15,
    timezone: 'America/New_York',
    is_active: true,
    slug: ''
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const [profileRes, bookingRes] = await Promise.all([
          api.get('/api/users/me/profile'),
          api.get('/api/staff/availability').catch(() => ({ data: { availability: null } }))
        ]);
        
        setProfile({
          first_name: profileRes.data.first_name || '',
          last_name: profileRes.data.last_name || '',
          email: profileRes.data.email || '',
          phone: profileRes.data.phone || '',
          avatar: profileRes.data.avatar || '',
          admin_pin: profileRes.data.admin_pin || '',
          chat_available: profileRes.data.chat_available || false,
          chat_availability: profileRes.data.chat_availability || {}
        });
        
        if (bookingRes.data.availability) {
          setBookingAvailability(bookingRes.data.availability);
        }
      } catch (error) {
        // Fallback to user from context
        if (user) {
          setProfile({
            first_name: user.first_name || '',
            last_name: user.last_name || '',
            email: user.email || '',
            phone: user.phone || '',
            avatar: user.avatar || '',
            chat_available: user.chat_available || false,
            chat_availability: user.chat_availability || {}
          });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [api, user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/users/me/profile', profile);
      toast.success('Profile updated successfully');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBookingAvailability = async () => {
    setSavingBooking(true);
    try {
      await api.post('/api/staff/availability', bookingAvailability);
      toast.success('Booking schedule saved');
      // Refresh to get the slug
      const res = await api.get('/api/staff/availability');
      if (res.data.availability) {
        setBookingAvailability(res.data.availability);
      }
    } catch (error) {
      toast.error('Failed to save booking schedule');
    } finally {
      setSavingBooking(false);
    }
  };

  const handleBookingDayToggle = (dayId) => {
    setBookingAvailability(prev => ({
      ...prev,
      available_days: {
        ...prev.available_days,
        [dayId]: {
          ...prev.available_days[dayId],
          enabled: !prev.available_days[dayId]?.enabled,
          slots: !prev.available_days[dayId]?.enabled ? [{ start: '09:00', end: '17:00' }] : []
        }
      }
    }));
  };

  const handleBookingTimeChange = (dayId, field, value) => {
    setBookingAvailability(prev => ({
      ...prev,
      available_days: {
        ...prev.available_days,
        [dayId]: {
          ...prev.available_days[dayId],
          slots: [{
            ...prev.available_days[dayId]?.slots?.[0],
            [field]: value
          }]
        }
      }
    }));
  };

  const toggleBookingDuration = (duration) => {
    setBookingAvailability(prev => ({
      ...prev,
      appointment_durations: prev.appointment_durations.includes(duration)
        ? prev.appointment_durations.filter(d => d !== duration)
        : [...prev.appointment_durations, duration].sort((a, b) => a - b)
    }));
  };

  const copyBookingLink = () => {
    if (bookingAvailability.slug) {
      navigator.clipboard.writeText(`${window.location.origin}/booking/${bookingAvailability.slug}`);
      toast.success('Booking link copied!');
    }
  };

  const handleDayToggle = (dayId) => {
    const current = profile.chat_availability[dayId];
    if (current?.enabled) {
      // Disable the day
      setProfile({
        ...profile,
        chat_availability: {
          ...profile.chat_availability,
          [dayId]: { ...current, enabled: false }
        }
      });
    } else {
      // Enable with default hours (9 AM - 5 PM)
      setProfile({
        ...profile,
        chat_availability: {
          ...profile.chat_availability,
          [dayId]: { enabled: true, start: '09:00', end: '17:00' }
        }
      });
    }
  };

  const handleTimeChange = (dayId, field, value) => {
    setProfile({
      ...profile,
      chat_availability: {
        ...profile.chat_availability,
        [dayId]: {
          ...profile.chat_availability[dayId],
          [field]: value
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1628]">My Profile</h1>
          <p className="text-gray-500">Manage your personal information and chat availability</p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-[#014DB7] hover:bg-[#003A8C] text-white rounded-xl"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            {/* Avatar */}
            <div className="text-center mb-6">
              <div className="relative inline-block">
                <div className="w-24 h-24 bg-gradient-to-br from-[#014DB7] to-[#84CC16] rounded-full flex items-center justify-center text-white text-3xl font-bold">
                  {profile.first_name?.[0]}{profile.last_name?.[0]}
                </div>
                <button className="absolute bottom-0 right-0 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-sm hover:bg-gray-50">
                  <Camera className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-[#0A1628]">
                {profile.first_name} {profile.last_name}
              </h3>
              <p className="text-sm text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>

            {/* Quick Stats */}
            <div className="space-y-3 border-t border-gray-200 pt-4">
              <div className="flex items-center gap-3 text-sm">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600 truncate">{profile.email}</span>
              </div>
              {profile.phone && (
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">{profile.phone}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Shield className="w-4 h-4 text-gray-400" />
                <span className="text-gray-600 capitalize">{user?.role?.replace('_', ' ')} Access</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Info */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-[#0A1628] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#014DB7]" />
              Personal Information
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  className="rounded-xl"
                  placeholder="Enter first name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  className="rounded-xl"
                  placeholder="Enter last name"
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={profile.email}
                  disabled
                  className="rounded-xl bg-gray-50"
                />
                <p className="text-xs text-gray-400">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  value={profile.phone}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  className="rounded-xl"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label>Admin PIN</Label>
                <Input
                  type="password"
                  value={profile.admin_pin}
                  onChange={(e) => setProfile({ ...profile, admin_pin: e.target.value })}
                  className="rounded-xl"
                  placeholder="4-6 digit PIN"
                  maxLength={6}
                />
                <p className="text-xs text-gray-400">Used to unlock sent quotes for editing</p>
              </div>
            </div>
          </div>

          {/* Chat Availability */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#0A1628] flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#84CC16]" />
                Chat Availability
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Available for Live Chat</span>
                <Switch
                  checked={profile.chat_available}
                  onCheckedChange={(checked) => setProfile({ ...profile, chat_available: checked })}
                />
              </div>
            </div>

            {profile.chat_available && (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Set your weekly schedule for when you're available to handle live chat requests.
                </p>

                <div className="space-y-3">
                  {DAYS_OF_WEEK.map(day => {
                    const dayConfig = profile.chat_availability[day.id] || { enabled: false };
                    
                    return (
                      <div 
                        key={day.id}
                        className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                          dayConfig.enabled 
                            ? 'border-[#84CC16] bg-[#84CC16]/5' 
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        <button
                          onClick={() => handleDayToggle(day.id)}
                          className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                            dayConfig.enabled 
                              ? 'bg-[#84CC16] text-white' 
                              : 'bg-white border border-gray-200 text-gray-400'
                          }`}
                        >
                          {dayConfig.enabled ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                        </button>
                        
                        <span className={`w-24 font-medium ${dayConfig.enabled ? 'text-[#0A1628]' : 'text-gray-400'}`}>
                          {day.label}
                        </span>

                        {dayConfig.enabled ? (
                          <div className="flex items-center gap-2 flex-1">
                            <select
                              value={dayConfig.start || '09:00'}
                              onChange={(e) => handleTimeChange(day.id, 'start', e.target.value)}
                              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30"
                            >
                              {TIME_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                            <span className="text-gray-400">to</span>
                            <select
                              value={dayConfig.end || '17:00'}
                              onChange={(e) => handleTimeChange(day.id, 'end', e.target.value)}
                              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30"
                            >
                              {TIME_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">Not available</span>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#014DB7] mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-[#014DB7]">Timezone: Your Local Time</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Your availability is shown to the chat system based on these hours. 
                        You'll only receive chat assignments during your available hours.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {!profile.chat_available && (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <MessageCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Enable chat availability to set your schedule</p>
              </div>
            )}
          </div>

          {/* Booking Schedule Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-[#0A1628] flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-[#014DB7]" />
                Booking Schedule
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-500">Accept Bookings</span>
                <Switch
                  checked={bookingAvailability.is_active}
                  onCheckedChange={(checked) => setBookingAvailability({ ...bookingAvailability, is_active: checked })}
                />
              </div>
            </div>

            {/* Booking URL */}
            {bookingAvailability.slug && (
              <div className="mb-4 p-3 bg-blue-50 rounded-xl border border-blue-200 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-900">Your Booking Page</p>
                  <p className="text-sm text-blue-600 font-mono">{window.location.origin}/booking/{bookingAvailability.slug}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={copyBookingLink} className="p-2 hover:bg-blue-100 rounded-lg transition-colors">
                    <Copy className="w-4 h-4 text-blue-600" />
                  </button>
                  <a 
                    href={`/booking/${bookingAvailability.slug}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-4 h-4 text-blue-600" />
                  </a>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-500 mb-4">
              Set your availability for clients to book meetings with you.
            </p>

            {/* Appointment Durations */}
            <div className="mb-6">
              <Label className="text-sm font-medium mb-2 block">Appointment Durations</Label>
              <div className="flex flex-wrap gap-2">
                {[15, 30, 45, 60].map(d => (
                  <button
                    key={d}
                    onClick={() => toggleBookingDuration(d)}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      bookingAvailability.appointment_durations.includes(d)
                        ? 'bg-[#014DB7] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            </div>

            {/* Buffer Time */}
            <div className="mb-6">
              <Label className="text-sm font-medium mb-2 block">Buffer Between Meetings</Label>
              <div className="flex flex-wrap gap-2">
                {[15, 30, 45, 60].map(b => (
                  <button
                    key={b}
                    onClick={() => setBookingAvailability({ ...bookingAvailability, buffer_time: b })}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      bookingAvailability.buffer_time === b
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {b} min
                  </button>
                ))}
              </div>
            </div>

            {/* Weekly Schedule */}
            <div className="space-y-3">
              {DAYS_OF_WEEK.map(day => {
                const dayConfig = bookingAvailability.available_days?.[day.id] || { enabled: false, slots: [] };
                const slot = dayConfig.slots?.[0] || { start: '09:00', end: '17:00' };
                
                return (
                  <div 
                    key={day.id}
                    className={`flex items-center gap-4 p-3 rounded-xl border transition-all ${
                      dayConfig.enabled 
                        ? 'border-[#014DB7] bg-blue-50/50' 
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <button
                      onClick={() => handleBookingDayToggle(day.id)}
                      className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
                        dayConfig.enabled 
                          ? 'bg-[#014DB7] text-white' 
                          : 'bg-white border border-gray-200 text-gray-400'
                      }`}
                    >
                      {dayConfig.enabled ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                    
                    <span className={`w-24 font-medium ${dayConfig.enabled ? 'text-[#0A1628]' : 'text-gray-400'}`}>
                      {day.label}
                    </span>

                    {dayConfig.enabled ? (
                      <div className="flex items-center gap-2 flex-1">
                        <select
                          value={slot.start}
                          onChange={(e) => handleBookingTimeChange(day.id, 'start', e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
                        >
                          {TIME_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <span className="text-gray-400">to</span>
                        <select
                          value={slot.end}
                          onChange={(e) => handleBookingTimeChange(day.id, 'end', e.target.value)}
                          className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
                        >
                          {TIME_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">Not available</span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Save Button */}
            <div className="mt-6 flex justify-end">
              <Button 
                onClick={handleSaveBookingAvailability} 
                disabled={savingBooking}
                className="bg-[#014DB7] hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingBooking ? 'Saving...' : 'Save Booking Schedule'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
